/**
 * Grants Module - Shared Types
 *
 * This file contains shared type definitions for the Grants module (KiedyNabory).
 * These types are used across multiple components to ensure type consistency.
 */

/**
 * Represents a single grant call item with all associated data.
 * Used by CallCard and CallsList components to display grant call information.
 */
export interface CallItem {
  id: string;
  title: string;
  status: string;
  callType: string;
  grantType: string | null;
  applicationStartDate: string | null;
  applicationEndDate: string | null;
  announcedAt: string | null;
  fundingRange: {
    min?: number;
    max?: number;
    percentage?: number;
    currency: string;
  } | null;
  allocation: number | null;
  targetRegions: string[];
  targetGroups: string[];
  officialUrl: string | null;
  applicationUrl: string | null;
  program: {
    id: string;
    code: string;
    name: string;
    shortName: string;
    /** Program capabilities (e.g., ['LABOUR_BAROMETER'] for KFS) */
    capabilities?: string[];
  } | null;
  operator: {
    id: string;
    name: string;
    shortName: string | null;
    type: string;
  } | null;
  /**
   * Competition data (denormalized from Project).
   * Contains competition UUID for filtering and display data (name, code).
   * Can be null if call is not associated with a competition (e.g., KFS calls).
   */
  competition?: {
    id: string;
    name: string;
    code?: string | null;
    actionCode?: string | null;
  } | null;
  project: {
    id: string;
    name: string;
    code?: string | null;
    /** TERYT district codes from project.subregions */
    subregions?: string[];
  } | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Pagination state and metadata for grant calls list
 */
export interface Pagination {
  currentPage: number;
  totalPages: number;
  limit: number;
  total: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Count of calls grouped by status
 */
export interface StatusCount {
  status: string;
  count: number;
}

/**
 * Count of calls grouped by program
 */
export interface ProgramCount {
  id: string;
  code: string;
  name: string;
  count: number;
}

/**
 * Count of calls grouped by competition (for faceted search)
 * Per ADR-027: id is used for filtering, code/name for display
 */
export interface CompetitionCount {
  id: string;
  code: string;
  name: string;
  count: number;
}

/**
 * Count of calls grouped by target group (for faceted search)
 * Used in advanced filters to show how many calls match each target group.
 * Filtering uses OR logic per ADR-027.
 *
 * @see apps/backend/src/grants/shared/target-group.types.ts - TargetGroupType enum
 * @see ADR-027 Faceted Search Pattern
 */
export interface TargetGroupCount {
  targetGroup: string;  // TargetGroupType enum value (e.g., 'SME', 'ADULT')
  label: string;        // Human-readable label (e.g., 'MŚP', 'Osoby dorosłe')
  count: number;        // Number of calls with this target group
}

/**
 * Count of calls grouped by district (for faceted search)
 * Per ADR-027: slug is used for filtering, name for display
 *
 * @see docs/specifications/2026-01-27-kfs-phase2-calls-filtering-by-district.md
 */
export interface DistrictCount {
  slug: string;        // District slug (API identifier, e.g., "wroclaw")
  name: string;        // Display name with Polish chars (e.g., "Wrocław")
  voivodeship: string; // Region slug (e.g., "dolnoslaskie")
  count: number;       // Number of calls in this district
}

/**
 * Label count for faceted search in calls list.
 * Returned by GET /grants/calls in labelCounts field.
 */
export interface LabelCount {
  id: string;
  name: string;
  color: string;
  count: number;
}

// =====================================================
// Barometer Types (BC: Catalog)
// @see docs/specifications/2026-03-06-kfs-barometr-zawodow.md
// =====================================================

/**
 * District barometer data - deficit professions for a single district
 */
export interface BarometerDistrict {
  code: string;
  name: string;
  voivodeship: string;
  largeShortageProfessions: string[];
  shortageProfessions: string[];
}

/**
 * Voivodeship-level barometer data
 */
export interface BarometerVoivodeship {
  name: string;
  largeShortageProfessions: string[];
  shortageProfessions: string[];
}

/**
 * Response from GET /grants/barometer endpoint
 */
export interface BarometerResponse {
  year: number;
  districts: BarometerDistrict[];
  voivodeship?: BarometerVoivodeship;
}

// =====================================================
// Alert Types (BC: Alerts)
// @see docs/specifications/2026-01-14-alerts-notification-service.md
// @see docs/specifications/2026-01-22-alerts-ux-simplification.md
// =====================================================

/**
 * Alert type discriminator — distinguishes event-driven alerts from digest alerts.
 *
 * @see docs/specifications/2026-05-26-upcoming-calls-digest-alert/spec.md
 */
export enum AlertType {
  /** Classic event-driven alert (new call / change) */
  EVENT_DRIVEN = 'EVENT_DRIVEN',
  /** Cyclic digest of upcoming calls */
  UPCOMING_DIGEST = 'UPCOMING_DIGEST',
}

/**
 * Digest frequency — interval in weeks between digest sends.
 *
 * @see docs/specifications/2026-05-26-upcoming-calls-digest-alert/spec.md — "Rozstrzygnięte decyzje"
 */
export enum DigestFrequency {
  /** Every week (1 week interval) */
  WEEKLY = 1,
  /** Every two weeks (2 week interval) */
  BIWEEKLY = 2,
  /** Every four weeks (4 week interval — NOT "co miesiąc") */
  FOUR_WEEKS = 4,
}

/**
 * Polish labels for DigestFrequency values.
 * Exact labels as resolved in spec "Rozstrzygnięte decyzje":
 * - 4 → "Co 4 tygodnie" (NOT "Co miesiąc")
 */
export const DIGEST_FREQUENCY_LABELS: Record<DigestFrequency, string> = {
  [DigestFrequency.WEEKLY]: 'Co tydzień',
  [DigestFrequency.BIWEEKLY]: 'Co dwa tygodnie',
  [DigestFrequency.FOUR_WEEKS]: 'Co 4 tygodnie',
};

/**
 * Polish labels for days of week (0 = Sunday, 1 = Monday … 6 = Saturday).
 */
export const DAY_OF_WEEK_LABELS: Record<number, string> = {
  0: 'Niedziela',
  1: 'Poniedziałek',
  2: 'Wtorek',
  3: 'Środa',
  4: 'Czwartek',
  5: 'Piątek',
  6: 'Sobota',
};

/**
 * Alert preset - simplified user-facing option for event selection
 *
 * @see docs/specifications/2026-01-22-alerts-ux-simplification.md
 * @see ADR-029: Presets Over Granularity
 */
export enum AlertPreset {
  /** Only new calls */
  NEW_CALLS_ONLY = 'new_calls_only',
  /** New calls + changes (default) */
  NEW_AND_CHANGES = 'new_and_changes',
  /** Everything including announcements */
  EVERYTHING = 'everything',
}

/**
 * Alert event types - events that can trigger alerts
 * @deprecated Use AlertPreset instead for UI - eventTypes are derived from preset
 */
export enum AlertEventType {
  CALL_CREATED = 'call_created',
  CALL_CHANGED = 'call_changed', // NEW
  NEW_ANNOUNCEMENT = 'new_announcement', // NEW
  // DEPRECATED
  CALL_UPDATED = 'call_updated',
  CALL_STATUS_CHANGED = 'call_status_changed',
  ARTICLE_MAPPED = 'article_mapped',
}

/**
 * Alert granularity - determines which events match the alert
 * @deprecated Granularity is ignored in new preset-based model
 */
export enum AlertGranularity {
  ALL = 'all',
  EVERY_ARTICLE = 'every_article',
  STATUS_ONLY = 'status_only',
  DATES_ONLY = 'dates_only',
  OPENING_CLOSING = 'opening_closing',
  OPENING_ONLY = 'opening_only',
}

/**
 * Aggregation mode for email notifications
 */
export enum AggregationMode {
  SEPARATE = 'SEPARATE',
  CONSOLIDATED = 'CONSOLIDATED',
}

/**
 * Alert filters - criteria for matching events
 *
 * @see docs/specifications/2026-01-23-alert-filters-competitions-projects.md
 */
export interface AlertFilters {
  programs?: string[];
  operators?: string[];
  regions?: string[];
  /** Filter by competitions (AND with other filters) */
  competitionIds?: string[];
  /** Filter by projects (AND with other filters) */
  projectIds?: string[];
  /** Filter by district slugs for KFS calls (AND with other filters, OR within) */
  districtSlugs?: string[];
  /** @deprecated Hidden in UI but kept for backward compatibility */
  minAllocation?: number;
  /** @deprecated Hidden in UI but kept for backward compatibility */
  maxAllocation?: number;
}

/**
 * Notification schedule - when to send notifications
 */
export interface NotificationSchedule {
  mode: 'IMMEDIATE' | 'SCHEDULED';
  hours: number[];
  timezone: string;
}

/**
 * Reason a digest alert's next send will be skipped, computed server-side from the
 * current billing capability. `null`/`undefined` = no gate, digest will be sent on
 * the next cycle. Always `null` for EVENT_DRIVEN alerts (the capability gate applies
 * only to UPCOMING_DIGEST), and always `null` when `isEnabled === false` (the gate is
 * irrelevant for an alert the user already turned off). Closed enum — precedence
 * CHECK_FAILED > NO_CAPABILITY > OVER_CAPACITY (first gate that will actually stop
 * the send).
 *
 * @see docs/specifications/2026-07-13-digest-entitlement-soft-gate/spec.md AC-5
 */
export enum DigestEntitlementBlockedReason {
  /** Tenant has no digest capability at all (maxDigestAlerts === 0) */
  NO_CAPABILITY = 'NO_CAPABILITY',
  /** Tenant has capability but this alert is over the per-user digest limit */
  OVER_CAPACITY = 'OVER_CAPACITY',
  /** Transient billing capability check failure (fail-closed, not an entitlement fact) */
  CHECK_FAILED = 'CHECK_FAILED',
}

/**
 * Alert item returned from API. Mirrors backend `AlertResponseDto` — returned by
 * POST/PUT/GET /:id. Deliberately has NO `entitlementBlockedReason`: that field is
 * only ever computed on `GET /grants/alerts` (list), where the capability lookup +
 * ranking has already been done once for the whole request. See `AlertListItem`.
 *
 * @see docs/specifications/2026-01-22-alerts-ux-simplification.md
 * @see docs/specifications/2026-05-26-upcoming-calls-digest-alert/spec.md
 * @see docs/specifications/2026-07-13-digest-entitlement-soft-gate/spec.md
 */
export interface AlertItem {
  id: string;
  name: string;
  /**
   * Alert type discriminator.
   * Defaults to EVENT_DRIVEN for legacy alerts that do not return this field.
   */
  type?: AlertType;
  /** User-selected preset - simplified option that determines eventTypes */
  preset: AlertPreset;
  filters: AlertFilters;
  /** Event types derived from preset (read-only in new model) */
  eventTypes: AlertEventType[];
  /** @deprecated Granularity is ignored in new preset-based model */
  granularity: AlertGranularity;
  schedule: NotificationSchedule;
  /**
   * Digest schedule fields — only present when type === UPCOMING_DIGEST.
   * frequency: interval in weeks (1 | 2 | 4)
   * dayOfWeek: 0 (Sunday) – 6 (Saturday)
   */
  frequency?: DigestFrequency | null;
  dayOfWeek?: number | null;
  isEnabled: boolean;
  matchCount: number;
  lastMatchAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Alert item as returned by `GET /grants/alerts` (list). Mirrors backend
 * `AlertListItemResponseDto extends AlertResponseDto` — adds the digest
 * entitlement gate state, which is only computed for the list endpoint.
 *
 * @see docs/specifications/2026-07-13-digest-entitlement-soft-gate/spec.md AC-5
 */
export interface AlertListItem extends AlertItem {
  /**
   * Digest capability gate state (UPCOMING_DIGEST only). `null` = no gate, digest
   * will be sent on the next cycle. Always `null` for EVENT_DRIVEN alerts and for
   * disabled digests (the gate is irrelevant if the alert would not send regardless).
   */
  entitlementBlockedReason: DigestEntitlementBlockedReason | null;
}

/**
 * Alert list response from API
 */
export interface AlertListResponse {
  alerts: AlertListItem[];
  limit: number;
  remaining: number;
  /**
   * Remaining slots for UPCOMING_DIGEST alerts (limit: 1 per account).
   * If undefined (old backend), treat as available (digestRemaining > 0).
   *
   * @see docs/specifications/2026-05-26-upcoming-calls-digest-alert/spec.md
   */
  digestRemaining?: number;
}

/**
 * Payload for creating an event-driven alert
 *
 * @see docs/specifications/2026-01-22-alerts-ux-simplification.md
 */
export interface CreateEventDrivenAlertPayload {
  type: AlertType.EVENT_DRIVEN;
  name: string;
  /** User-selected preset - determines eventTypes automatically */
  preset: AlertPreset;
  filters?: AlertFilters;
  schedule?: NotificationSchedule;
}

/**
 * Payload for creating a digest (upcoming calls) alert
 *
 * @see docs/specifications/2026-05-26-upcoming-calls-digest-alert/spec.md
 */
export interface CreateDigestAlertPayload {
  type: AlertType.UPCOMING_DIGEST;
  name: string;
  /** Interval in weeks: 1 | 2 | 4 */
  frequency: DigestFrequency;
  /** Day of week: 0 (Sunday) – 6 (Saturday) */
  dayOfWeek: number;
  filters?: AlertFilters;
  schedule?: NotificationSchedule;
}

/**
 * Payload for creating an alert (union of both types)
 *
 * @see docs/specifications/2026-01-22-alerts-ux-simplification.md
 * @see docs/specifications/2026-05-26-upcoming-calls-digest-alert/spec.md
 */
export type CreateAlertPayload = CreateEventDrivenAlertPayload | CreateDigestAlertPayload;

/**
 * Payload for updating an alert
 *
 * @see docs/specifications/2026-01-22-alerts-ux-simplification.md
 * @see docs/specifications/2026-05-26-upcoming-calls-digest-alert/spec.md
 */
export interface UpdateAlertPayload {
  name?: string;
  type?: AlertType;
  /** User-selected preset - determines eventTypes automatically */
  preset?: AlertPreset;
  /** Interval in weeks (digest only): 1 | 2 | 4 */
  frequency?: DigestFrequency | null;
  /** Day of week (digest only): 0–6 */
  dayOfWeek?: number | null;
  filters?: AlertFilters;
  schedule?: NotificationSchedule;
  isEnabled?: boolean;
}

/**
 * Notification settings for the current user
 */
export interface NotificationSettings {
  aggregationMode: AggregationMode;
  globalEnabled: boolean;
  bounceCount: number;
  isEmailValid: boolean;
  sendingPaused: boolean;
  sendingPausedReason: string | null;
}

/**
 * Payload for updating notification settings
 */
export interface UpdateNotificationSettingsPayload {
  aggregationMode?: AggregationMode;
  globalEnabled?: boolean;
}
